<template>
  <component :is="props.scroll ? xHorizontalScroll : 'div'" class="x-chip-menu">
    <!-- eslint-disable-next-line vuejs-accessibility/label-has-for -->
    <label class="chip-menu-label">
      <slot />
    </label>
    <div class="chip-container">
      <ul :class="{ 'use-scroll': props.scroll }">
        <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -->
        <li
          v-if="props.allChip"
          :class="{ 'selected-chip': selected.has(ALL) }"
          data-test="chip-all"
          @click="handleAllClick"
        >
          {{ ALL }}
        </li>
        <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -->
        <li
          v-for="(chip, index) in chipsToShow"
          :key="index"
          :class="{ 'selected-chip': selected.has(chip.label) }"
          :data-test="`chip-${index}`"
          @click="handleClick(chip.label)"
        >
          <x-icon
            v-if="chip.icon"
            :icon="chip.icon"
            width="20"
            height="20"
            class="chip-icon"
          />
          <span class="chip-label">{{ chip.label }}</span>
        </li>
        <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -->
        <li
          v-if="showExpandChip"
          :class="{
            'expand-chip': !showCompleteList,
            'collapse-chip': showCompleteList,
          }"
          @click="handleExpandToggle"
        >
          <x-icon
            :icon="expandChipIcon"
            color="var(--ys-color-icon-primary)"
            width="20"
            height="20"
            class="chip-icon"
          />
          <span class="chip-label">{{ expandChipText }}</span>
        </li>
      </ul>
    </div>
  </component>
</template>

<script lang="ts">
/* eslint-disable vue/component-api-style */
/**
 * @deprecated Use `<ys-menu-chip>` from the latest `@onxmaps/yellowstone-ui` release instead.
 */
export default {
  name: 'XChipMenu',
}
</script>

<script setup lang="ts">
import { ref, computed, onMounted, watch, watchEffect } from 'vue'
import xHorizontalScroll from '@/components/shared/x-horizontal-scroll/index.vue'
import xIcon from '@/components/shared/x-icon/index.vue'

type ChipWithIcon = {
  icon?: string
  label: string
}

type XChipMenuProps = {
  chips: Array<ChipWithIcon>
  // eslint-disable-next-line vue/no-unused-properties
  scroll?: boolean
  multiSelect?: boolean
  allChip?: boolean
  /** Allows the menu to be able to re-enter a state with nothing selected for single select menus */
  unselectable?: boolean
  expandChipIndex?: number
  initialSelection?: string[]
  showSelectedFirst?: boolean
}

const ALL = 'All'

const props = withDefaults(defineProps<XChipMenuProps>(), {
  scroll: false,
  multiSelect: false,
  allChip: false,
  unselectable: false,
  expandChipIndex: -1,
  initialSelection: () => [],
  showSelectedFirst: false,
})

const emit = defineEmits<{
  (e: 'click', value: string): void
}>()

const chipLabels = computed(() => {
  return props.chips.map((c) => c.label)
})

const selected = ref(new Set<string>())

const showExpandChip = computed(
  () => props.expandChipIndex > -1 && props.expandChipIndex < props.chips.length
)
const showCompleteList = ref(!showExpandChip.value)
const expandChipIcon = computed(() => {
  return showCompleteList.value ? 'search/delete' : 'plus'
})
const expandChipText = computed(() => {
  return showCompleteList.value ? 'Less' : 'More'
})

const chipsToShow = computed(() => {
  return showCompleteList.value
    ? sortedChips.value
    : sortedChips.value.slice(0, props.expandChipIndex)
})

const sortedChips = computed(() => {
  return props.showSelectedFirst ? sortChips([...props.chips]) : props.chips
})

/** Functions **/

const handleClick = (chip: string) => {
  if (selected.value.has(chip) && (props.multiSelect || props.unselectable)) {
    // deselect if already selected
    selected.value.delete(chip)

    if (selected.value.size === 0 && props.allChip) {
      // if everything has been deselected, select All chip
      selected.value.add(ALL)
    }
  } else {
    if (!props.multiSelect || selected.value.has(ALL)) {
      selected.value.clear()
    }

    selected.value.add(chip)
  }

  emit('click', chip)
}

const handleAllClick = () => {
  selected.value.clear()
  selected.value.add(ALL)
  emit('click', ALL)
}

const handleExpandToggle = () =>
  (showCompleteList.value = !showCompleteList.value)

const setInitialSelection = (chipsToSelect: string[]) => {
  if (props.multiSelect) {
    chipsToSelect.forEach((chip) => {
      if (chip !== ALL) {
        selected.value.add(chip)
      }
    })
  } else {
    // use the first chip from initialSelection
    selected.value.add(chipsToSelect[0])
  }
}

const sortChips = (chips: Array<ChipWithIcon>) => {
  return chips.sort((chipA, chipB) => {
    if (!selected.value.has(chipA.label) && selected.value.has(chipB.label)) {
      return 1
    }
    return -1
  })
}

/** Lifecycle **/

onMounted(() => {
  if (props.allChip && props.initialSelection.length === 0) {
    selected.value.add(ALL)
  }
})

/** Watchers **/

watch(
  () => props.chips,
  () => {
    // check to make sure all selected chips are in the new list of chips
    if (!selected.value.has(ALL)) {
      selected.value.forEach((selectedItem) => {
        if (!chipLabels.value.includes(selectedItem)) {
          selected.value.delete(selectedItem)
        }
      })
    }

    // if all selected chips were removed and All is enabled, select All
    if (props.allChip && selected.value.size === 0) {
      selected.value.add(ALL)
      emit('click', ALL)
    }
  }
)

watchEffect(() => {
  if (props.initialSelection.length > 0) {
    setInitialSelection(props.initialSelection)
  }
})
</script>

<style lang="scss" scoped>
@use '@/styles/system/_colors.scss';
@use '@/styles/system/_type.scss';

.x-chip-menu {
  position: relative;
  padding: 0px 20px;

  .chip-menu-label {
    margin-top: 20px;
    @include type.body3;
  }

  .chip-container {
    overflow: hidden;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    gap: 8px;
    flex-wrap: wrap;

    &.use-scroll {
      flex-wrap: nowrap;
      white-space: nowrap;
      width: fit-content;
    }

    li {
      @include type.body1;
      display: flex;
      height: 40px;
      align-items: center;
      padding: 8px 16px;
      border: 2px solid colors.$Grey30;
      border-radius: 20px;
      cursor: pointer;
      user-select: none;

      &.selected-chip {
        border-color: colors.$Grey100;
        color: colors.$BaseWhite;
        background-color: colors.$Grey100;

        .chip-icon {
          :deep(svg) {
            fill: var(--ys-color-icon-reverse);
          }
        }
      }

      .chip-icon {
        line-height: 0;
        position: relative;
        left: -4px;

        :deep(svg) {
          fill: var(--ys-color-icon-primary);
        }
      }

      .chip-label {
        line-height: 1;
      }
    }
  }
}
</style>
